import { MouseEventHandler, useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { ComponentHeader } from "./ComponentHeader";
import { ComponentContainer } from "./ComponentContainer";
import { Router, useRouter } from "next/router";
import {
  getLeaderboardByPoolId,
  getLeaderboardByPoolIdAll,
} from "../api/leaderboards";
import toast from "react-hot-toast";
import { addFriendRequest, removeFriendRequest } from "../api/user";

// import faker from "@faker-js/faker";
// import { List, AutoSizer } from "react-virtualized";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import GolfSpinner from "./Icons/GolfSpinner";
import { formatScore, ordinalValue } from "../lib/helpers";
// const fakeData = new Array(50000).fill(50000).map((value, index) => ({
//   id: index.toString(),
//   firstName: faker.name.firstName(),
//   lastName: faker.name.lastName(),
//   rank: index,
//   score: parseInt(faker.random.numeric(5)),
//   friend: false,
//   myself: false,
// }));
// console.log(fakeData);

const insert = (arr, index, newItem) => [
  ...arr.slice(0, index),
  newItem,
  ...arr.slice(index),
];

//Forcing commit
const LeaderboardItem = (
  id: string,
  firstName: string,
  lastName: string,
  rank: number,
  score: number,
  friend: boolean,
  myself: boolean,
  onUserEntryPress: Function,
  onAddFriend: MouseEventHandler<HTMLButtonElement>,
  onRemoveFriend: MouseEventHandler<HTMLButtonElement>,
) => {
  const friendIcon = () => (
    <div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 ml-1 mt-1"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
        />
      </svg>
    </div>
  );

  return (
    <>
      <div
        key={id}
        className={`leaderboardItem flex flex-row pl-2 hover:bg-gray-100  p-0.5 shadow-inner ${
          myself ? "bg-green-300" : null
        } `}
      >
        {rank && (
          <div className="font-serif w-12 mr-2">
            {rank}
            <sup>{ordinalValue(rank)}</sup>
          </div>
        )}
        <div className="flex flex-row justify-between grow">
          <div className="flex flex-row">
            <span
              onClick={(e) => {
                e.preventDefault();
                onUserEntryPress(id);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                onUserEntryPress(id);
              }}
              className="hover:cursor-pointer font-serif whitespace-nowrap capitalize"
            >
              {firstName.toLocaleLowerCase()} {lastName.toLocaleLowerCase()}
            </span>
            {friend ? friendIcon() : null}
          </div>
          {!myself &&
            (friend ? (
              <button
                className={`removeFriendButton font-serif align-middle cursor-pointer hover:bg-red-600 hover:text-white text-black bg-white h-6 pl-1 pr-1 rounded`}
                onClick={onRemoveFriend}
              >
                Unfollow
              </button>
            ) : (
              <button
                className={`addFriendButton font-serif align-middle cursor-pointer hover:bg-green-600 hover:text-white text-black bg-white h-6 pl-1 pr-1 rounded`}
                onClick={onAddFriend}
              >
                Follow
              </button>
            ))}
        </div>
        {score && (
          <div className="font-serif w-10 text-right">{formatScore(score)}</div>
        )}
      </div>
    </>
  );
};

const Leaderboard = (props) => {
  const { data: session }: any = useSession();
  const accessToken = session?.accessToken;

  const [searchText, setSearchText] = useState("");
  const [showAll, setShowAll] = useState(false);

  const router = useRouter();
  const [allUserList, setAllUserList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const {
    isLoading: leaderboardLoading,
    error: leaderboardError,
    data: userList,
  } = useQuery(
    ["leadboard", props.id],
    () => getLeaderboardByPoolId(accessToken, props.id),
    {
      enabled: !!accessToken,
    },
  );

  // const { isLoading, error, data } = useQuery("articles", () => getArticles(), {
  //   enabled: !!accessToken,
  // });

  const showTopWithFriends = useMutation(
    () => getLeaderboardByPoolId(accessToken, props.id),
    {
      onSuccess: (data, variables, context) => {
        let firstIndex = data.findIndex((el) => {
          return el.rank > 5 && el.friend;
        });
        data = insert(data, firstIndex, {
          header: true,
          firstName: "",
          lastName: "",
        });

        // setUserList(data);
        setIsLoading(false);
      },
      onError: (error: Error) => {
        toast.error(`An error occurred while adding friend - ${error.message}`);
      },
    },
  );

  const { isFetching: showAllUsersIsFetching } = useQuery({
    queryKey: ["showAllUsers", props.id as string],
    queryFn: () => getLeaderboardByPoolIdAll(accessToken, props.id),
    onSuccess: (data) => {
      setAllUserList(data);
      setIsLoading(false);
      return data;
    },
    onError: (error: Error) => {
      toast.error(`An error occurred while adding friend - ${error.message}`);
    },
    enabled: showAll,
  });

  const addFriend = useMutation(
    (userId) => addFriendRequest(accessToken, userId),
    {
      onSuccess: (data, variables, context) => {
        const newList = userList.map((u) => {
          if (u.id == variables) u.friend = true;
          return u;
        });
        setAllUserList(newList);
        toast.success(`The user added as a friend`);
      },
      onError: (error: Error) => {
        toast.error(`An error occurred while adding friend - ${error.message}`);
      },
    },
  );

  const removeFriend = useMutation(
    (userId) => removeFriendRequest(accessToken, userId),
    {
      onSuccess: (data, variables, context) => {
        let newList: any[] = [];
        if (showAll) {
          newList = userList.map((u) => {
            if (u.id == variables) u.friend = false;
            return u;
          });
        } else {
          newList = userList;
          const userIndex = userList.findIndex((u) => u.id == variables);
          if (userIndex > 5) newList.splice(userIndex, 1);
          else newList[userIndex].friend = false;
        }
        setAllUserList(newList);
        toast.success(`The user has been unfriended`);
      },
      onError: (error: Error) => {
        toast.error(
          `An error occurred while removing friend - ${error.message}`,
        );
      },
    },
  );

  let searchInputHandler = (e) => {
    var lowerCase = e.target.value.toLowerCase();
    setSearchText(lowerCase);
  };

  const currentUserList = showAll ? allUserList : userList;

  let filteredData =
    currentUserList &&
    currentUserList.filter((el) => {
      return `${el.firstName.toLowerCase()} ${el.lastName.toLowerCase()}`.includes(
        searchText,
      );
    });

  // let filteredData = fakeData;

  let rowHeight = ({ index }) => (filteredData[index].header ? 48 : 30);

  const ROW = ({ index, style }) => {
    const item = filteredData[index];
    return (
      <div style={style} key={index}>
        {item.header ? (
          <div className={`relative flex py-3 items-center`}>
            <div className="flex-grow border-t border-gray-400"></div>
            <span className="flex-shrink mx-4 text-gray-400">Friends</span>
            <div className="flex-grow border-t border-gray-400"></div>
          </div>
        ) : (
          LeaderboardItem(
            item.id,
            item.firstName,
            item.lastName,
            item.rank,
            item.score,
            item.friend,
            item.myself,
            props.showEntryPress,
            (e) => {
              e.preventDefault();
              addFriend.mutate(item.id);
            },
            (e) => {
              e.preventDefault();
              removeFriend.mutate(item.id);
            },
          )
        )}
      </div>
    );
  };

  const LEADERBOARD_HEADER = "Leaderboard";

  const leaderboardDetails = () => (
    <>
      {showAll && (
        <div className="mb-3 w-full">
          <input
            className="appearance-none border-2 w-full font-serif py-1 px-3 text-gray-700 focus:outline-none  focus:shadow-outline"
            id="usersearch"
            type="text"
            placeholder="Search for a friend"
            onChange={searchInputHandler}
          ></input>
        </div>
      )}

      {isLoading || showAllUsersIsFetching ? (
        <div style={{ minHeight: defaultHeight + "px" }}>
          <div className="flex justify-center">
            <GolfSpinner width="30" height="30" />
          </div>
        </div>
      ) : (
        <div style={{ minHeight: defaultHeight + "px" }}>
          <List
            height={getHeight()}
            itemCount={filteredData.length}
            itemSize={(index) => rowHeight({ index })}
          >
            {ROW}
          </List>
        </div>
      )}
    </>
  );

  const getHeight = () => {
    if (!userList || userList?.length <= 0) {
      return 200;
    }

    const userRowHeight = 28;
    const maxLeaderboardHeight = 2000;
    const userLength = userList.length * userRowHeight + 20;
    if (userLength > maxLeaderboardHeight) {
      console.log("user length > maxLeaderboardHeight");
      return maxLeaderboardHeight;
    } else {
      console.log("user length < maxLeaderboardHeight");
      return userLength;
    }
  };

  const defaultHeight = getHeight();

  if (!props?.tournament?.status)
    return (
      <div className="flex justify-center">
        <GolfSpinner width="30" height="30" />
      </div>
    );

  return (
    <div className="relative">
      <div className='h-[50px] bg-black text-white flex items-center px-[12px] font-["Montserrat"] justify-between rounded-[5px]'>
        <span className="font-bold text-[16px] flex-1">
          {props.tournament?.status === "OPEN"
            ? "Pool Entrants"
            : "LEADERBOARD"}
        </span>
        <span className="font-normal text-[12px] mr-[8px]">
          {props?.userCount || 0} USERS
        </span>
        {userList && userList.length > 0 && (
          <span>
            <button
              className={`text-sm align-middle cursor-pointer hover:bg-blue-600 hover:text-white text-black bg-white px-2 py-1 rounded`}
              onClick={() => {
                if (showAll) {
                  showTopWithFriends.mutate();
                  setSearchText("");
                } else {
                  // showAllUsers.mutate();
                }
                setIsLoading(true);
                setShowAll(!showAll);
              }}
            >
              {`Show ${showAll ? "Less" : "All"}`}
            </button>
          </span>
        )}
      </div>
      {userList && userList.length > 0 ? (
        leaderboardDetails()
      ) : (
        <div className="flex justify-center mt-2">
          <GolfSpinner width="30" height="30" />
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
